import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import { EMPTY_FORM } from './createOffer/constants';
import OfferMandateStep from './createOffer/OfferMandateStep';
import OfferBuyersStep from './createOffer/OfferBuyersStep';
import OfferAmountStep from './createOffer/OfferAmountStep';
import OfferReviewStep from './createOffer/OfferReviewStep';

const STEP_LABELS = {
    1: 'Bien concerné',
    2: 'Acquéreur(s)',
    3: 'Montant & financement',
    4: 'Récapitulatif',
};

export default function CreateOfferModal({ isOpen, onClose, onSuccess, preselectedMandateId }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [mandates, setMandates] = useState([]);
    const [mandatesLoading, setMandatesLoading] = useState(true);
    const [mandateSearch, setMandateSearch] = useState('');

    const [form, setForm] = useState({
        ...EMPTY_FORM,
        mandate_id: preselectedMandateId || '',
        buyers: [{ ...EMPTY_FORM.buyers[0] }],
    });

    useEffect(() => {
        if (isOpen) {
            loadMandates();
            if (preselectedMandateId) {
                setForm((f) => ({ ...f, mandate_id: preselectedMandateId }));
            }
        }
    }, [isOpen, preselectedMandateId]);

    const loadMandates = async () => {
        setMandatesLoading(true);
        try {
            const res = await api.get('/offers/agency/mandates');
            setMandates(res.data.mandates || []);
        } catch {
            toast.error('Erreur chargement des mandats');
        } finally {
            setMandatesLoading(false);
        }
    };

    const selectedMandate = mandates.find((m) => m.id === form.mandate_id);

    const canProceed = () => {
        if (step === 1) return !!form.mandate_id;
        if (step === 2) {
            const b = form.buyers[0];
            return b.first_name && b.last_name && b.email && b.phone;
        }
        if (step === 3) return !!form.offer_amount && parseFloat(form.offer_amount) > 0;
        return true;
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = {
                mandate_id: form.mandate_id,
                offer_amount: parseFloat(form.offer_amount),
                validity_days: form.validity_days,
                buyers: form.buyers.map((b) => ({
                    first_name: b.first_name,
                    last_name: b.last_name,
                    email: b.email,
                    phone: b.phone,
                    address: b.address || undefined,
                    city: b.city || undefined,
                    postal_code: b.postal_code || undefined,
                })),
                financing: {
                    type: form.financing.type,
                    personal_contribution: form.financing.personal_contribution
                        ? parseFloat(form.financing.personal_contribution)
                        : undefined,
                    loan_amount: form.financing.loan_amount
                        ? parseFloat(form.financing.loan_amount)
                        : undefined,
                    loan_duration_months: form.financing.loan_duration_months || undefined,
                    has_pre_approval: form.financing.has_pre_approval,
                    bank_name: form.financing.bank_name || undefined,
                },
                conditions: form.conditions,
                desired_signature_date: form.desired_signature_date || undefined,
                personal_message: form.personal_message || undefined,
            };

            const res = await api.post('/offers/', payload);
            toast.success(`Offre ${res.data.offer_number} créée avec succès`);

            // Automatically send for signature
            try {
                await api.post(`/offers/${res.data.offer_id}/send-for-signature`);
                toast.success('Offre envoyée pour signature électronique');
            } catch {
                toast.info('Offre créée. Envoyez-la manuellement pour signature.');
            }

            onSuccess?.();
            handleClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setForm({ ...EMPTY_FORM, buyers: [{ ...EMPTY_FORM.buyers[0] }] });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={handleClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="create-offer-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-white">Nouvelle offre d'achat</h2>
                            <p className="text-xs text-white/40 mt-0.5">
                                Étape {step}/4 — {STEP_LABELS[step]}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-lg hover:bg-white/10 text-white/60"
                            data-testid="offer-close-btn"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Progress */}
                    <div className="flex gap-1 px-4 sm:px-6 pt-3">
                        {[1, 2, 3, 4].map((s) => (
                            <div
                                key={`progress-${s}`}
                                className={`h-1 flex-1 rounded-full transition-colors ${
                                    s <= step
                                        ? 'bg-gradient-to-r from-[#681793] to-[#FF914D]'
                                        : 'bg-white/10'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                        {step === 1 && (
                            <OfferMandateStep
                                form={form}
                                setForm={setForm}
                                mandateSearch={mandateSearch}
                                setMandateSearch={setMandateSearch}
                                mandates={mandates}
                                mandatesLoading={mandatesLoading}
                            />
                        )}
                        {step === 2 && <OfferBuyersStep form={form} setForm={setForm} />}
                        {step === 3 && (
                            <OfferAmountStep form={form} setForm={setForm} selectedMandate={selectedMandate} />
                        )}
                        {step === 4 && <OfferReviewStep form={form} selectedMandate={selectedMandate} />}
                    </div>

                    {/* Footer */}
                    <div className="p-4 sm:p-6 border-t border-white/10 flex items-center justify-between gap-3">
                        {step > 1 ? (
                            <Button
                                onClick={() => setStep((s) => s - 1)}
                                variant="ghost"
                                className="text-white/60 hover:text-white"
                                data-testid="offer-step-back"
                            >
                                Retour
                            </Button>
                        ) : (
                            <div />
                        )}

                        {step < 4 ? (
                            <Button
                                onClick={() => setStep((s) => s + 1)}
                                disabled={!canProceed()}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold px-6 disabled:opacity-40"
                                data-testid="offer-step-next"
                            >
                                Suivant
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSubmit}
                                disabled={loading}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold px-6"
                                data-testid="offer-submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <FileText className="w-4 h-4 mr-2" />
                                )}
                                Envoyer l'offre pour signature
                            </Button>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
